import { Fragment, useEffect, useId, useRef, useState, type KeyboardEvent, type ReactElement } from "react";
import { BusyDots } from "./BusyDots.tsx";
import { classes } from "./classes.ts";
import { focused } from "./focus.ts";
import { Divider } from "./frames.tsx";
import { Glyph, type GlyphName } from "./Glyph.tsx";
import { useKitInput } from "./input.tsx";
import { useInteraction } from "./interaction.tsx";
import { KeyGlyph } from "./KeyGlyph.tsx";
import { GLYPH_SIZES, KIT_METRICS, logical, menuItemLayout } from "./metrics.ts";
import { useWholeWidth } from "./measure.ts";
import { keepLayer } from "./modal-layer.ts";
import { AT_REST, choiceForKey, nextEnabled } from "./state.ts";

/**
 * A menu (brief section 5.1): a column of actions in the HUD frame, such as the game menu. While it is
 * open it is the topmost layer of the input router, so Escape closes it through the router, and it holds
 * the keyboard, so no key reaches the game behind it. It focuses its first item that can be chosen as it
 * opens; the up and down arrows, Home and End move between items, skipping disabled ones and wrapping;
 * Enter or Space chooses the focused item, as a click or a tap does; and focus goes back, as it closes, to
 * what had it as it opened. Every item is a button, so each action has a pointer and touch path beside its
 * keys (brief rule 23). A menu is one tab stop: Tab or Shift+Tab leaves it and closes it, so a menu never
 * stays open holding the keyboard while focus is on a control behind it, and focus moves on from the control
 * that opened it. An item whose action is still running (the game menu's Log out) is busy, as a busy button
 * is: its label stays in place, hidden under three blinking dots, so it keeps its size, and it stays
 * focusable but refuses to be chosen again. While an item is busy its page keeps the menu open, as a busy
 * confirm dialog refuses to close, so Tab and Shift+Tab leave focus on the menu rather than on a control
 * behind a menu that still holds the keyboard.
 *
 * As the design draws the game menu (ADR-061's amendment "the game menu, character select, loading and confirm
 * dialog match the design"), an item may lead with an 8 px glyph, end with the key glyph of a key that does the
 * same (Resume's Esc), be drawn in the danger colour, and stand after a divider. The divider is not an item and
 * takes no focus, so the keyboard moves through the items alone. A context menu may stand a heading above its items,
 * as the design's player menu names its player: the pixel face in the accent colour, cut short with an ellipsis, and
 * no tab stop.
 *
 * As the design's item menu draws a refused entry (ADR-125 decision 7.1), a disabled item may say why: its reason
 * stands at the item's right in the small body face, in the disabled text colour, and is the item's description for
 * assistive technology. Such an item is refused rather than disabled: it stays focusable and in the arrow keys' path,
 * marks itself `aria-disabled`, shows only its focus and does nothing when chosen, so a keyboard or screen reader user
 * reaches the reason a pointer user reads.
 *
 * As the design's Filter menu draws its choices (ADR-125 decision 2.3), an item may carry a note at its right, a count,
 * in the small body face, and a menu of choices may mark the current one: an item given `checked` is a
 * `menuitemradio` whose `aria-checked` says whether it is the one chosen, and the chosen one leads with the 8 px check.
 */

/**
 * One item of a menu.
 */
export interface MenuEntry<T extends string> {
  readonly value: T;
  readonly label: string;
  readonly disabled?: boolean;
  readonly busy?: boolean;
  /**
   * The glyph the item leads with.
   */
  readonly glyph?: GlyphName;
  /**
   * The name of a key that does what the item does, shown as a key glyph at the item's right.
   */
  readonly hint?: string;
  /**
   * Whether the item is drawn in the danger colour, as an action that ends something is.
   */
  readonly danger?: boolean;
  /**
   * Whether a divider stands before the item.
   */
  readonly divided?: boolean;
  /**
   * Why a disabled item cannot be chosen now, drawn at its right and read as its description. Ignored on an item that
   * is not disabled.
   */
  readonly why?: string;
  /**
   * A short note drawn at the item's right in the small body face, such as how many things the choice holds, read as
   * the item's description. A refused item's reason stands in its place.
   */
  readonly note?: string;
  /**
   * Whether the item is the menu's current choice, for a menu of choices: given, the item is a `menuitemradio`, and
   * the chosen one leads with the check in place of its glyph.
   */
  readonly checked?: boolean;
}

/**
 * One item: a `menuitem` button with its label in the pixel face, after its glyph and before its key glyph when
 * it has them; the key glyph is hidden from assistive technology, so the item is named by its label alone. Hover and a press show on its background, keyboard focus as the aether outline inside its box, a
 * danger item in the danger colour, and a disabled item in the disabled text colour. A busy item shows only its
 * focus, marks itself `aria-busy` and `aria-disabled`, and ignores being chosen; it is not disabled, so it keeps
 * focus and its place as the tab stop while its action runs. A disabled item that says why is refused: it keeps its
 * focus, marks itself `aria-disabled` and is described by its reason, drawn at its right, and ignores being chosen.
 * The glyph, the label, the reason and the key glyph are each set in the middle of the item on whole logical pixels
 * (`menuItemLayout`); the reason's box runs from the label to the item's padding, its text set at its right, so the
 * box stays on the grid whatever its text measures.
 *
 * @param props The item's label; its `glyph`, key `hint` and `danger`, when it has them; whether its menu carries
 *   a key hint on any item (`hinted`), which sets every item's height; whether it is disabled, and `why` when it says
 *   why; whether it is busy or the menu's tab stop; its `note`, and whether it is the menu's current choice
 *   (`checked`), for a menu of choices; a ref setter for moving focus to it; `onPick`, run when it is
 *   chosen; and `onFocus`, run when it takes focus.
 * @returns The item.
 */
export function MenuItem({ label, glyph, hint, danger = false, hinted = false, disabled = false, why, busy = false, note, checked, tabStop, refer, onPick, onFocus }: { readonly label: string; readonly glyph?: GlyphName | undefined; readonly hint?: string | undefined; readonly danger?: boolean; readonly hinted?: boolean; readonly disabled?: boolean; readonly why?: string | undefined; readonly busy?: boolean; readonly note?: string | undefined; readonly checked?: boolean | undefined; readonly tabStop: boolean; readonly refer?: (element: HTMLButtonElement | null) => void; readonly onPick: () => void; readonly onFocus?: () => void }): ReactElement {
  const refused = disabled && why !== undefined;
  const reasonId = useId();
  const { state, handlers } = useInteraction({ disabled: disabled && !refused, focus: "visible" });
  const shown = busy || refused ? { ...AT_REST, focus: state.focus } : state;
  const metrics = KIT_METRICS.menu;
  const lead = checked === true ? "check" : glyph;
  const layout = menuItemLayout({ hinted }, { glyph: lead === undefined ? null : GLYPH_SIZES[lead] });
  return (
    <button
      ref={refer}
      type="button"
      role={checked === undefined ? "menuitem" : "menuitemradio"}
      aria-checked={checked}
            tabIndex={tabStop ? 0 : -1}
      disabled={disabled && !refused}
      aria-busy={busy ? true : undefined}
      aria-disabled={busy || refused ? true : undefined}
      aria-describedby={refused || note !== undefined ? reasonId : undefined}
      className={classes("ot-menu__item", shown.hover && "is-hover", shown.pressed && "is-pressed", shown.focus && "is-focus", danger && "is-danger", disabled && "is-disabled", busy && "is-busy")}
      data-kit="menu-item"
      onClick={busy || refused ? undefined : onPick}
      style={{ height: logical(layout.height), paddingLeft: logical(metrics.paddingX), paddingRight: logical(metrics.paddingX), gap: logical(metrics.glyphGap) }}
      {...handlers}
      onFocus={(event) => {
        handlers.onFocus(event);
        onFocus?.();
      }}
    >
      {lead === undefined ? null : (
        <span className="ot-menu__glyph" style={{ marginTop: logical(layout.glyphTop) }}>
          <Glyph name={lead} />
        </span>
      )}
      <span className="ot-menu__label" style={{ marginTop: logical(layout.labelTop) }}>
        {label}
      </span>
      {refused || note !== undefined ? (
        <span id={reasonId} className="ot-menu__why" data-kit={refused ? "menu-why" : "menu-note"} style={{ marginTop: logical(layout.whyTop) }}>
          {refused ? why : note}
        </span>
      ) : null}
      {hint === undefined || refused ? null : (
        <span className="ot-menu__hint" aria-hidden="true" style={{ marginTop: logical(layout.keyTop) }}>
          <KeyGlyph>{hint}</KeyGlyph>
        </span>
      )}
      {busy ? <BusyDots top={layout.busyTop} left={layout.busyLeft} /> : null}
    </button>
  );
}

/**
 * An open menu.
 *
 * @param props The menu's accessible `label`; its `items`, any of them busy while its action runs;
 *   `onSelect` with the value of the item chosen; `onClose`, which Escape, and Tab or Shift+Tab while no item
 *   is busy, call to ask the page to close the menu; and its `width` in logical pixels, which otherwise
 *   follows its widest item, and its `minWidth`, the least width it takes however narrow its items are; a menu with no `width` takes its widest item's width rounded up to a whole logical pixel, measured once for its labels, so every box in it stays on the grid; and its
 *   `heading`, drawn above its items when given.
 * @returns The menu.
 * @throws Error when mounted outside a `KitInputProvider`.
 */
export function Menu<T extends string>({ label, items, onSelect, onClose, width, minWidth, heading }: { readonly label: string; readonly items: readonly MenuEntry<T>[]; readonly onSelect: (value: T) => void; readonly onClose: () => void; readonly width?: number; readonly minWidth?: number; readonly heading?: string }): ReactElement {
  const input = useKitInput();
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);
  const latestClose = useRef(onClose);
  const [opener] = useState(focused);
  const disabled = items.map((item) => item.disabled === true && item.why === undefined);
  const running = items.some((item) => item.busy === true);
  const first = nextEnabled(disabled, -1, 1);
  const [active, setActive] = useState(first);
  const tabStop = active >= 0 && disabled[active] === false ? active : first;

  useEffect(() => {
    latestClose.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const dropLayer = keepLayer(input, () => latestClose.current());
    const release = input.hold();
    return () => {
      dropLayer();
      release();
      if (opener instanceof HTMLElement && opener.isConnected) {
        opener.focus();
      }
    };
  }, [input, opener]);

  useEffect(() => {
    if (first >= 0) {
      buttons.current[first]?.focus();
    }
  }, []);

  /**
   * Moves focus with the up and down arrows, Home and End, and closes the menu on Tab or Shift+Tab. Tab is
   * stopped at the menu, so the router never passes it to the game once the menu has let the keyboard go,
   * and never prevented, so the browser still moves focus on from the control that opened the menu. While
   * an item is busy Tab asks nothing and is prevented: the page keeps the menu open until the item's action
   * ends, and focus stays on the menu, which still holds the keyboard.
   *
   * @param event The key press on an item.
   */
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === "Tab") {
      event.stopPropagation();
      if (running) {
        event.preventDefault();
        return;
      }
      latestClose.current();
      return;
    }
    const from = buttons.current.findIndex((button) => button === event.target);
    const to = choiceForKey(event.key, disabled, from, "vertical");
    if (to === null || to < 0) {
      return;
    }
    event.preventDefault();
    setActive(to);
    buttons.current[to]?.focus();
  };

  const hinted = items.some((item) => item.hint !== undefined);
  const root = useRef<HTMLDivElement>(null);
  const natural = items.map((item) => `${item.label}\u0001${item.hint ?? ""}\u0001${item.why ?? ""}\u0001${item.note ?? ""}`).join("\u0002") + `\u0003${heading ?? ""}\u0003${minWidth ?? ""}`;
  const snappedWidth = useWholeWidth(root, natural, width === undefined);

  return (
    <div ref={root} role="menu" aria-label={label} aria-orientation="vertical" className="ot-menu ot-ui-frame-hud" data-kit="menu" style={{ minWidth: minWidth === undefined ? undefined : logical(minWidth), width: width !== undefined ? logical(width) : snappedWidth === null ? undefined : logical(snappedWidth), padding: logical(KIT_METRICS.menu.padding) }} onKeyDown={onKeyDown}>
      {heading === undefined ? null : (
        <div className="ot-menu__heading" data-kit="menu-heading">
          {heading}
        </div>
      )}
      {items.map((item, index) => (
        <Fragment key={item.value}>
          {item.divided === true && index > 0 ? <Divider margin={KIT_METRICS.menu.dividerMargin} /> : null}
          <MenuItem
            label={item.label}
            glyph={item.glyph}
            hint={item.hint}
            danger={item.danger === true}
            hinted={hinted}
            disabled={item.disabled === true}
            why={item.why}
            busy={item.busy === true}
            note={item.note}
            checked={item.checked}
            tabStop={index === tabStop}
            refer={(element) => {
              buttons.current[index] = element;
            }}
            onPick={() => onSelect(item.value)}
            onFocus={() => setActive(index)}
          />
        </Fragment>
      ))}
    </div>
  );
}
